<script setup lang="ts">
import type { BreadcrumbProps } from '#seo-utils/app/composables/useBreadcrumbItems'
import type { PodcastRssFeed } from '#shared/utils/podcast'
import { faApple, faSpotify } from '@fortawesome/free-brands-svg-icons'

definePageMeta({
  name: 'podcast',
  layout: false,
  colorMode: 'light',
  theme: 'blurple',
})

const route = useRoute<'podcast'>()

const { data: podcast, suspense: waitForPodcastData } = usePodcast(() => ensureStringParam(route.params.slug))

await waitForPodcastData()

if (!podcast.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Podkast ikke funnet',
    fatal: true,
    data: {
      navigationContext: 'marketing' satisfies NavigationContext,
    },
  })
}

const rssSlug = computed(() => {
  const provider = podcast.value?.components?.providers?.acast
  if (provider) {
    const { pathname } = new URL(provider)
    return pathname.slice(1)
  }

  return null
})

const { data: rss, suspense: waitForRssData } = useQuery({
  enabled: () => !!toValue(rssSlug),
  queryKey: ['podcast-rss', rssSlug] as const,
  queryFn: ({ queryKey }) => $fetch<PodcastRssFeed>(`/api/podcast-rss/${queryKey[1]!}`),
})

await waitForRssData()

const { $htmlToText } = useNuxtApp()

const title = computed(() => $htmlToText(rss.value?.title))

useSeoMeta({
  title,
  description: computed(
    () => $htmlToText(rss.value?.description, [removeAcastDisclaimer, 'remove-empty-nodes']),
  ),
})

const breadcrumbs: BreadcrumbProps['overrides'] = computed(() => {
  return [
    undefined,
    false,
    title.value
      ? {
          label: title.value,
          to: route.path,
        }
      : undefined,
  ]
})

const years = useArrayUnique(() =>
  (rss.value?.items ?? [])
    .map(child => new Date(child.isoDate).getFullYear())
    .sort((a, b) => b - a),
)

const yearFilter = useRouteQuery<string | undefined>('year', () => {
  const latest = years.value[0]
  return latest ? String(latest) : undefined
})

const episodes = computed(() => {
  const items = rss.value?.items ?? []

  if (yearFilter.value) {
    return items.filter(item => new Date(item.isoDate).getFullYear().toString() === yearFilter.value)
  }

  return items
})

const drawerOpen = ref(false)

watch(yearFilter, () => {
  drawerOpen.value = false
})

useHead({
  bodyAttrs: {
    class: 'bg-blurple-50',
  },
})
</script>

<template>
  <NuxtLayout name="nettbutikk" :overrides="breadcrumbs" class="overflow-x-hidden">
    <ContentGrid class="[--before-bg:theme(colors.blurple.500)] mt-5">
      <header class="full-width grid grid-cols-subgrid">
        <div class="col-span-4 lg:col-span-3 2xl:col-span-5 grid grid-cols-subgrid z-[1] h-fit 2xl:bg-blurple-500">
          <div class="2xl:col-start-2 rounded-r-[6rem] sm:rounded-r-9xl md:rounded-r-none col-span-3 lg:col-span-full text-moss-200 py-m md:py-12 lg:py-18 z-10 bg-blurple-500">
            <h1 v-text="title" />

            <div v-if="podcast?.components?.providers" class="flex flex-col gap-4 py-m font-light">
              <span class="title-s !font-light">Lytt til podkasten der du vil:</span>

              <nav class="flex items-center gap-l text-base">
                <PodcastProvider
                  v-if="podcast?.components?.providers?.apple"
                  label="Apple&nbsp;podcasts"
                  :icon="faApple"
                  :provider="podcast.components.providers.apple"
                />
                <PodcastProvider
                  v-if="podcast?.components?.providers?.spotify"
                  label="Spotify"
                  :icon="faSpotify"
                  :provider="podcast.components.providers.spotify"
                />
                <PodcastProvider
                  v-if="podcast?.components?.providers?.acast"
                  label="Acast"
                  :icon="usePublicAssetUrl('/images/acast-logo.svg')"
                  :provider="podcast.components.providers.acast"
                />
              </nav>
            </div>
          </div>
          <Shape
            class="fill-blurple-500 h-[calc(100%+3px)] -mt-px lg:hidden absolute right-[--gutter]"
            variant="octagon"
          />
        </div>

        <div class="gap-l hidden z-0 lg:flex [--shift:180%] 2xl:[--shift:280%] -ml-[--shift] mr-[--shift]">
          <div class="h-full aspect-square bg-[linear-gradient(to_right,theme(colors.blurple.500)_50%,transparent_50%)]">
            <Shape
              class="fill-blurple-500 col-span-2 -mt-px"
              variant="octagon"
            />
          </div>

          <CrystallizeClippedImage
            v-if="podcast?.components?.headerImage?.firstImage"
            :image="podcast?.components?.headerImage?.firstImage"
            class="aspect-square min-h-full"
            path="decagon"
            img-class="object-cover object-center size-full"
          />

          <div class="h-full aspect-square">
            <Shape variant="circle" class="fill-moss-200" />
          </div>
        </div>
      </header>
    </ContentGrid>

    <div class="lg:!hidden bg-blurple-100 rounded-2xl mt-5 py-5 px-3.5 md:py-9 flex gap-s">
      <CrystallizeImage
        v-if="podcast?.components?.coverImage"
        :image="podcast.components.coverImage"
        img-class="rounded-xl"
        class="min-w-20 w-20 md:min-w-32 md:w-20"
      />

      <div v-if="podcast?.components?.description?.length" class="md:self-center">
        <CrystallizeRichText
          v-for="(block, i) in podcast.components.description"
          :key="i"
          :block="block"
          class="text-xsmall md:text-lg"
        />
      </div>
      <div
        v-else-if="rss?.description"
        class="text-xsmall md:text-lg col-span-3 self-center"
        v-html="rss.description"
      />
    </div>

    <ContentGrid class="bg-white rounded-2xl lg:rounded-4xl py-5 md:py-9 my-7 gap-y-3.5">
      <nav class="col-span-full lg:hidden flex flex-col gap-3">
        <span class="font-semibold">Publisert:</span>
        <PodcastYearFilter v-model="yearFilter" :years="years" orientation="horizontal" />
      </nav>

      <aside class="hidden lg:flex flex-col gap-6 lg:col-span-3 mr-20">
        <CrystallizeImage
          v-if="podcast?.components?.coverImage"
          :image="podcast.components.coverImage"
          img-class="rounded-2xl"
        />

        <template v-if="podcast?.components?.description?.length">
          <CrystallizeRichText
            v-for="(block, i) in podcast.components.description"
            :key="i"
            :block="block"
            class="text-lg"
          />
        </template>
        <div
          v-else-if="rss?.description"
          class="text-lg"
          v-html="rss.description"
        />

        <nav class="flex flex-col gap-2.5">
          <span class="font-semibold">
            Velg år
          </span>

          <PodcastYearFilter v-model="yearFilter" :years="years" orientation="vertical" />
        </nav>
      </aside>

      <PodcastEpisodes :episodes="episodes" class="col-span-full lg:col-span-5 2xl:col-span-9" />
    </ContentGrid>

    <PodcastDrawer
      v-model:open="drawerOpen"
      class="lg:hidden"
      :episodes="episodes"
    />
  </NuxtLayout>
</template>
